// AGC: Eruda mobile debugging console provider

import { useCallback, useEffect, useState } from 'react'
import { Copy } from 'lucide-react'
import { useDebugSettings } from '../hooks/useDebugSettings'
import { CapturedConsoleLog, ConsoleLevel, buildConsoleClipboardText } from '../utils/consoleLogs'

const MAX_CAPTURED_LOGS = 300

interface ErudaLike {
  show?: () => void
  hide?: () => void
  get?: (plugin: string) => { hide?: () => void }
  init: (config?: {
    defaults?: {
      displaySize?: number
      transparency?: number
      theme?: string
    }
    tool?: string[]
  }) => void
}

interface AgcWindow extends Window {
  eruda?: ErudaLike
  __agcConsoleLogs?: CapturedConsoleLog[]
  __agcConsolePatched?: boolean
  __agcAudioUnlocked?: boolean
}

type CopyMode = 'all' | 'errors'

type CopyStatus = 'idle' | 'copied' | 'error'

const formatConsoleArg = (arg: unknown): string => {
  if (typeof arg === 'string') return arg
  if (typeof arg === 'number' || typeof arg === 'boolean' || typeof arg === 'bigint') {
    return String(arg)
  }
  if (arg instanceof Error) {
    return `${arg.name}: ${arg.message}${arg.stack ? `\n${arg.stack}` : ''}`
  }
  try {
    return JSON.stringify(arg)
  } catch {
    return String(arg)
  }
}

export default function ErudaProvider() {
  const { debugSettings, isLoading: isDebugSettingsLoading } = useDebugSettings()
  const { erudaButtonEnabled, copyButtonEnabled } = debugSettings
  const [isLogCaptureReady, setIsLogCaptureReady] = useState(false)
  const [isErudaReady, setIsErudaReady] = useState(false)
  const [copyStatus, setCopyStatus] = useState<Record<CopyMode, CopyStatus>>({
    all: 'idle',
    errors: 'idle',
  })
  const shouldCaptureLogs = erudaButtonEnabled || copyButtonEnabled

  useEffect(() => {
    if (typeof window === 'undefined' || !shouldCaptureLogs || isDebugSettingsLoading) {
      setIsLogCaptureReady(false)
      return
    }

    const win = window as AgcWindow
    if (win.__agcConsolePatched) {
      setIsLogCaptureReady(true)
      return
    }

    win.__agcConsoleLogs = win.__agcConsoleLogs ?? []
    const levels: ConsoleLevel[] = ['log', 'info', 'warn', 'error']
    const restoreFns: Array<() => void> = []

    const consoleMethods = console as Record<ConsoleLevel, (...args: unknown[]) => void>

    levels.forEach((level) => {
      const original = consoleMethods[level]
      if (typeof original !== 'function') {
        return
      }

      consoleMethods[level] = ((...args: unknown[]) => {
        const message = args.map(formatConsoleArg).join(' ')
        const entry: CapturedConsoleLog = {
          level,
          message,
          timestamp: new Date().toISOString(),
        }

        win.__agcConsoleLogs = [...(win.__agcConsoleLogs ?? []), entry].slice(-MAX_CAPTURED_LOGS)
        return original(...args)
      }) as typeof original

      restoreFns.push(() => {
        consoleMethods[level] = original
      })
    })

    win.__agcConsolePatched = true
    setIsLogCaptureReady(true)

    return () => {
      restoreFns.forEach((restore) => restore())
      win.__agcConsolePatched = false
      setIsLogCaptureReady(false)
    }
  }, [shouldCaptureLogs, isDebugSettingsLoading])

  useEffect(() => {
    if (typeof window === 'undefined' || !erudaButtonEnabled || isDebugSettingsLoading) {
      const win = window as AgcWindow
      if (win.eruda) {
        win.eruda?.hide?.()
        console.log('🗑️ AGC: Eruda hidden.')
      }
      setIsErudaReady(false)
      return
    }

    let resizeHandler: (() => void) | null = null
    let styleElement: HTMLStyleElement | null = null

    import('eruda')
      .then((erudaModule) => {
        const win = window as AgcWindow
        if (!win.eruda) {
          win.eruda = erudaModule.default as ErudaLike
          const erudaInstance = win.eruda
          erudaInstance.init({
            defaults: {
              displaySize: 52,
              transparency: 0,
              theme: 'Material Dark',
            },
          })
          try {
            erudaModule.default?.config?.set?.('transparency', 0)
            erudaModule.default?.config?.set?.('displaySize', 52)
          } catch (configError) {
            console.warn('AGC: Unable to enforce Eruda transparency config, relying on CSS override.', configError)
          }
        } else {
          win.eruda.show?.()
        }

        styleElement = document.getElementById('agc-eruda-style') as HTMLStyleElement | null
        if (!styleElement) {
          styleElement = document.createElement('style')
          styleElement.id = 'agc-eruda-style'
          document.head.appendChild(styleElement)
        }
        styleElement.innerHTML = `
          .eruda-entry-btn {
            left: 16px !important;
            right: auto !important;
            bottom: 16px !important;
            opacity: 1 !important;
            background: #020617 !important;
            border: 2px solid #38bdf8 !important;
            box-shadow: 0 12px 30px rgba(2, 6, 23, 0.55);
          }
          .eruda-entry-btn .eruda-icon {
            filter: none !important;
          }
          .eruda-container,
          .eruda-container .eruda,
          .eruda-container .eruda-panel-wrap,
          .eruda-container .eruda-panel,
          .eruda-container .eruda-panel-container,
          .eruda-container .eruda-main,
          .eruda-container .eruda-console-panel,
          .eruda-container .eruda-console-panel .eruda-panel-content,
          .eruda-container .eruda-console-panel .eruda-log-container,
          .eruda-container .eruda-console-panel .eruda-log,
          .eruda-container .eruda-console-panel .eruda-toolbar,
          .eruda-container .eruda-console-panel .eruda-content,
          .eruda-container .eruda-console-panel .eruda-logs,
          .eruda-container .eruda-console-panel .eruda-log-item,
          .eruda-container .eruda-console-panel .eruda-log__body,
          .eruda-container .eruda-console-panel .eruda-log__content {
            background: #020617 !important;
            background-color: #020617 !important;
            background-image: none !important;
            backdrop-filter: none !important;
            opacity: 1 !important;
          }
          .eruda-container .eruda-console-panel .eruda-toolbar,
          .eruda-container .eruda-console-panel .eruda-content {
            box-shadow: none !important;
            border-color: rgba(15, 23, 42, 0.8) !important;
          }
          .eruda-container .eruda-nav-bar {
            background-color: #0f172a !important;
            border-bottom: 1px solid rgba(56, 189, 248, 0.25);
          }
          .eruda-container .eruda-console-panel .eruda-log.error {
            background-color: #471818 !important;
            border-left: 2px solid #f87171 !important;
          }
          .eruda-container .eruda-console-panel .eruda-log.warn {
            background-color: #433518 !important;
            border-left: 2px solid #fbbf24 !important;
          }
          .eruda-container .eruda-console-panel .eruda-log.info,
          .eruda-container .eruda-console-panel .eruda-log.log {
            background-color: rgba(2, 6, 23, 0.95) !important;
            border-left: 2px solid rgba(56, 189, 248, 0.35);
          }
          .eruda-container .eruda-console-panel .eruda-log {
            color: #e2e8f0 !important;
          }
        `

        const repositionButton = () => {
          const entryButton = document.querySelector<HTMLDivElement>('.eruda-entry-btn')
          if (entryButton) {
            entryButton.style.left = '16px'
            entryButton.style.right = 'auto'
            entryButton.style.bottom = '16px'
          }
        }

        repositionButton()
        resizeHandler = () => repositionButton()
        window.addEventListener('resize', resizeHandler)

        setIsErudaReady(true)
        console.log('✅ Eruda initialized for debugging')
      })
      .catch((error) => {
        console.error('AGC: Failed to initialize Eruda console', error)
      })

    return () => {
      if (resizeHandler) {
        window.removeEventListener('resize', resizeHandler)
      }
      if (styleElement) {
        styleElement.remove()
      }
      const win = window as AgcWindow
      if (win.eruda) {
        win.eruda?.hide?.()
        console.log('🗑️ AGC: Eruda hidden.')
      }
      setIsErudaReady(false)
    }
  }, [erudaButtonEnabled, isDebugSettingsLoading])

  const resetCopyStatus = useCallback((mode: CopyMode, delayMs: number) => {
    const schedule = typeof window !== 'undefined' ? window.setTimeout : setTimeout
    schedule(() => {
      setCopyStatus((prev) => ({ ...prev, [mode]: 'idle' }))
    }, delayMs)
  }, [])

  const handleCopyLogs = useCallback(
    async (mode: CopyMode) => {
      if (typeof window === 'undefined') {
        return
      }

      const win = window as AgcWindow
      const logs = win.__agcConsoleLogs ?? []
      const text =
        mode === 'errors'
          ? buildConsoleClipboardText(logs, {
              filterLevels: ['error'],
              emptyFallback: 'No error logs captured yet.',
            })
          : buildConsoleClipboardText(logs, {
              emptyFallback: 'No console logs captured yet.',
            })

      try {
        if (!navigator?.clipboard?.writeText) {
          throw new Error('Clipboard API unavailable in this context.')
        }
        await navigator.clipboard.writeText(text)
        setCopyStatus((prev) => ({ ...prev, [mode]: 'copied' }))
        resetCopyStatus(mode, 2000)
      } catch (error) {
        setCopyStatus((prev) => ({ ...prev, [mode]: 'error' }))
        console.error('AGC: Failed to copy console logs', error)
        resetCopyStatus(mode, 4000)
      }
    },
    [resetCopyStatus],
  )

  if (isDebugSettingsLoading || (!copyButtonEnabled && !erudaButtonEnabled)) {
    return null
  }

  return (
    <div className="pointer-events-none fixed top-4 right-4 z-[70] flex flex-col items-end gap-2">
      {copyButtonEnabled && (
        <div className="pointer-events-auto flex flex-col gap-2">
          <button
            type="button"
            onClick={() => handleCopyLogs('all')}
            disabled={!isLogCaptureReady}
            className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold shadow-lg transition-colors ${
              isLogCaptureReady
                ? 'bg-slate-900 text-white border-cyan-400 hover:bg-slate-800'
                : 'bg-slate-900/60 text-slate-500 border-slate-600 cursor-not-allowed'
            }`}
            title={isLogCaptureReady ? 'Copy captured console output' : 'Log capture is still initializing'}
          >
            <Copy className="h-4 w-4" />
            {copyStatus.all === 'copied'
              ? 'Copied!'
              : copyStatus.all === 'error'
                ? 'Copy failed'
                : 'Copy Console Log'}
          </button>
          <button
            type="button"
            onClick={() => handleCopyLogs('errors')}
            disabled={!isLogCaptureReady}
            className={`flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold shadow-lg transition-colors ${
              isLogCaptureReady
                ? 'bg-rose-900 text-white border-rose-400 hover:bg-rose-800'
                : 'bg-rose-900/60 text-rose-200/50 border-rose-700 cursor-not-allowed'
            }`}
            title={isLogCaptureReady ? 'Copy only error entries' : 'Log capture is still initializing'}
          >
            <Copy className="h-4 w-4" />
            {copyStatus.errors === 'copied'
              ? 'Errors copied!'
              : copyStatus.errors === 'error'
                ? 'Copy failed'
                : 'Copy Error Log'}
          </button>
        </div>
      )}
      {copyButtonEnabled && (
        <span className="pointer-events-none text-right text-xs text-slate-400">
          {isLogCaptureReady ? 'Log capture ready' : 'Preparing log capture...'}
          {erudaButtonEnabled && (
            <>
              {' • '}
              {isErudaReady ? 'Eruda console ready' : 'Loading Eruda console...'}
            </>
          )}
        </span>
      )}
    </div>
  )
}

declare global {
  interface Window {
    eruda?: ErudaLike
    __agcConsoleLogs?: CapturedConsoleLog[]
    __agcConsolePatched?: boolean
    __agcAudioUnlocked?: boolean
  }
}

